import React from 'react';
import { Typography, Box } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { DateTime } from 'luxon';
import { MoSpacing, MoHoverTooltip } from '@motivo/guanyin/src/components';
import { formatCentsAsDollars } from '@motivo/guanyin/src/utils/stringUtils';
import { ResultOf } from ':src/graphql';
import { organizationAdminDetailsByIdQuery } from '../../graphql/queries';
import { Section } from '../Section';

type OrganizationAdmin = NonNullable<
  ResultOf<typeof organizationAdminDetailsByIdQuery>['organizationAdminById']
>;

interface RatesSectionProps {
  organizationAdmin: OrganizationAdmin;
}

export default function RatesSection({ organizationAdmin }: RatesSectionProps) {
  const { organization } = organizationAdmin;
  const rate = organization.currentOrganizationRate;
  const nextRate = organization.nextOrganizationRate;

  const effectiveDate = rate.effectiveDate
    ? DateTime.fromISO(rate.effectiveDate).toFormat('MMM d, yyyy')
    : 'N/A';
  const nextRenewalDate = nextRate?.effectiveDate
    ? DateTime.fromISO(nextRate.effectiveDate).toFormat('MMM d, yyyy')
    : 'N/A';

  const rateTypes = [
    { label: 'Individual', amount: rate.individualRateTotalInCents },
    { label: 'Dyad', amount: rate.dyadRateTotalInCents },
    { label: 'Group', amount: rate.groupRateTotalInCents },
  ];

  return (
    <Section
      title="Rates"
      summaryLink={{
        label: 'View rates in Zeus →',
        path: `/organizations/${organization.id}?currentTab=billing`,
      }}
    >
      <MoSpacing y={2}>
        <Box display="flex" gap={3}>
          {rateTypes.map(({ label, amount }) => (
            <Box
              key={label}
              flex={1}
              border={1}
              borderColor="divider"
              borderRadius={2}
              p={2}
              textAlign="center"
            >
              <Typography variant="body3">
                {amount != null ? formatCentsAsDollars(amount) : 'N/A'}
              </Typography>
              <Typography variant="overline" color="textSecondary">
                {label}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography>
            Renews <strong>{nextRenewalDate}</strong>
          </Typography>
          <MoHoverTooltip titleContent={`Effective ${effectiveDate}`}>
            <InfoOutlined fontSize="small" color="action" />
          </MoHoverTooltip>
        </Box>
      </MoSpacing>
    </Section>
  );
}
